import { useState } from "react";
import type { JSX } from "react";

import { BrandMark, resolveBrand } from "../../components/ui";
import type { TagVariant } from "../../components/ui/Tag";
import type { ExtensionView } from "./types";

/**
 * Render the visual mark for an extension.
 *
 * Known brands (google/telegram/x/linkedin) reuse the SAME `BrandMark`
 * the Sources panel and Add Account picker render — one brand, one mark
 * everywhere. Every other package ships an `icon_url` (DEC-9) rendered
 * full-bleed — no white plate behind the art. No icon — OR an icon_url that
 * fails to load (many plugins declare `ui/icon.svg` but don't ship it → 404) —
 * falls back to the package's initial, never a broken-image glyph.
 */
export function ExtensionMark({
  ext,
  size,
}: {
  readonly ext: ExtensionView;
  readonly size: number;
}): JSX.Element {
  const [imgFailed, setImgFailed] = useState(false);
  const plainId = ext.id.split(".").pop() ?? ext.id;
  const brand = resolveBrand(plainId);
  return (
    <div
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      {brand ? (
        <BrandMark brand={brand} size={size} />
      ) : ext.icon_url && !imgFailed ? (
        <img
          src={ext.icon_url}
          alt=""
          width={size}
          height={size}
          className="rounded-full object-cover"
          style={{ width: size, height: size }}
          onError={() => { setImgFailed(true); }}
        />
      ) : (
        <div
          className="flex items-center justify-center rounded-full bg-white/95"
          style={{ width: size, height: size }}
        >
          <span
            className="font-semibold text-black/70"
            style={{ fontSize: Math.round(size * 0.4) }}
          >
            {ext.title.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function isPreviewLocked(ext: ExtensionView): boolean {
  return !ext.installed && ext.kind !== "skill";
}

// eslint-disable-next-line react-refresh/only-export-components
export function stateLabel(ext: ExtensionView): string {
  if (isPreviewLocked(ext)) return "Preview";
  if (!ext.installed) return "Available";
  return ext.enabled ? "Enabled" : "Disabled";
}

// eslint-disable-next-line react-refresh/only-export-components
export function kindVariant(kind: ExtensionView["kind"]): TagVariant {
  switch (kind) {
    case "module":
      return "blue";
    case "source":
      return "orange";
    case "skill":
      return "purple";
  }
}
